import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import Select from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import InputPhoneNumber from "components/ui/input/InputPhoneNumber";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { PERMISSION_TYPES } from "@routebot/constants";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

const typeValues = ["service", "insurance", "other"];

const InventoryThirdPartyItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { thirdParties } = useContext(StoreContext);
  const auth = useContext(AuthContext);

  const itemType = "thirdParty";
  const itemTextType = t("inventoryThirdParty.itemName");
  const endPoint = "third-party-contacts";

  const [formState, inputHandler] = useForm(
    {
      name: { value: "", isValid: false },
      type: { value: "service", isValid: true },
      phone: { value: "", isValid: true },
      countryCode: { value: "", isValid: true },
      email: { value: "", isValid: true },
      address: { value: "", isValid: true },
      notes: { value: "", isValid: true },
    },
    false
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const current = thirdParties.find((thirdParty) => thirdParty.id === itemId);
      setLoadedItem(current);
    } catch (err) {
      console.log(err);
    }
    setIsItemLoaded(true);
  }, [thirdParties, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const infoToBeSent = {
      name: formState.inputs.name.value,
      type: formState.inputs.type.value,
      phone: formState.inputs.phone.value,
      countryCode: formState.inputs.countryCode.value,
      email: formState.inputs.email.value,
      address: formState.inputs.address.value,
      notes: formState.inputs.notes.value,
    };

    try {
      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPoint}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPoint}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent), {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  const translatedTypeOptions = useMemo(
    () =>
      typeValues.map((value) => ({
        value,
        label: t(`inventoryThirdParty.types.${value}`),
      })),
    [t]
  );

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState((auth?.countryCode || "TR").toUpperCase());
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  useEffect(() => {
    if (loadedItem) {
      const phoneValue = loadedItem.phone || "";
      const codeValue = (loadedItem.countryCode || auth?.countryCode || "TR").toUpperCase();
      setPhoneNumber(phoneValue);
      setCountryCode(codeValue);
      setIsPhoneValid(true);
      inputHandler("phone", phoneValue, true);
      inputHandler("countryCode", codeValue, true);
    } else {
      const defaultCode = (auth?.countryCode || "TR").toUpperCase();
      setPhoneNumber("");
      setCountryCode(defaultCode);
      setIsPhoneValid(true);
      inputHandler("phone", "", true);
      inputHandler("countryCode", defaultCode, true);
    }
  }, [loadedItem, auth?.countryCode, inputHandler]);

  const handlePhoneChange = (value, newCountryCode, valid) => {
    const normalizedCode = (newCountryCode || countryCode || "TR").toUpperCase();
    setPhoneNumber(value || "");
    setCountryCode(normalizedCode);
    setIsPhoneValid(valid);
    inputHandler("phone", value || "", valid);
    inputHandler("countryCode", normalizedCode, true);
  };

  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTextType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTextType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }
  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPoint}
        itemType={itemTextType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container">
          <div className="horizontal-flex">
            <Input
              id="name"
              element="input"
              type="text"
              label={t("inventoryThirdParty.fields.name")}
              validators={[VALIDATOR_REQUIRE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.name || ""}
              initialValid={!isNewItem}
            />

            <Select
              id="type"
              label={t("inventoryThirdParty.fields.type")}
              options={translatedTypeOptions}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={
                translatedTypeOptions.find((option) => option.value === loadedItem?.type) || translatedTypeOptions[0]
              }
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <InputPhoneNumber
              id="phone"
              label={t("inventoryThirdParty.fields.phone")}
              phoneNumber={phoneNumber}
              countryCode={countryCode}
              onChange={handlePhoneChange}
              errorText={t("inventoryThirdParty.errors.invalidPhone")}
              language={auth.language || "tr"}
              hideLabel={auth?.countryCode?.toUpperCase() === countryCode}
            />

            <Input
              id="email"
              element="input"
              type="text"
              label={t("inventoryThirdParty.fields.email")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.email || ""}
              initialValid={true}
            />
          </div>

          <Input
            id="address"
            element="textarea"
            label={t("inventoryThirdParty.fields.address")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            rows={3}
            initialValue={loadedItem?.address || ""}
            initialValid={true}
          />

          <Input
            id="notes"
            element="textarea"
            label={t("inventoryThirdParty.fields.notes")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            rows={5}
            initialValue={loadedItem?.notes || ""}
            initialValid={true}
          />

          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || !isPhoneValid || isLoading}
            />
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryThirdPartyItem;
